import {
  type RunStepOut,
  streamCaseRun,
  zRunStepOut,
} from "@platform/api/golden";
import { logger } from "@platform/browser-logger";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { useGoldenClient } from "../api/GoldenClientProvider";

/** How long the view waits before it connects again after a stream closed or failed. */
export const STREAM_RECONNECT_MS = 2_000;

/** Statuses after which connecting again cannot help: not allowed, or no such run. */
const TERMINAL_STATUSES: ReadonlySet<number> = new Set([403, 404]);

/**
 * Connections in a row that may be refused as unauthenticated before the view
 * gives up: a token that expired during a long run is refused once, and the
 * next connection asks for a fresh one. A caller who stays signed out is
 * refused again.
 */
const UNAUTHORIZED_RETRIES = 1;

/**
 * Where the view's connection to the run's stream stands: connecting at first,
 * live once an event arrived, reconnecting after it failed or closed early,
 * unavailable once the API refused it for good, and ended once the run ended.
 */
export type StreamConnection =
  | "connecting"
  | "live"
  | "reconnecting"
  | "unavailable"
  | "ended";

export type RunStream = {
  /** The run's steps so far, oldest first. */
  readonly steps: readonly RunStepOut[];
  /** The run's state as the stream last reported it. */
  readonly state: string | undefined;
  readonly connection: StreamConnection;
  /** True once the stream sent an event the page could not read and dropped. */
  readonly droppedEvents: boolean;
};

// The events' shapes, checked before anything reaches the page: the generated
// client's validator is off for the stream (see below), so these stand in.
const zStep = zRunStepOut.extend({ seq: z.number().int().positive() });
const zRunEvent = z.object({ state: z.string() });
const zEndEvent = z.object({
  state: z.string().optional(),
  reason: z.string().optional(),
});

/** The status a stream's response failed with, from the generated client's error. */
const failedStatus = (error: unknown): number | undefined => {
  const match = /SSE failed: (\d{3})/.exec(
    error instanceof Error ? error.message : "",
  );
  return match ? Number(match[1]) : undefined;
};

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

/**
 * Follows a run's stream until it reports the run's end: its state, then each
 * step of its RUMI run as RUMI works. A stream that closes or fails before the
 * end is opened again after the last step it sent, with Last-Event-ID, so no
 * step is lost or repeated.
 */
export const useRunStream = (
  caseId: number,
  runId: number,
  onEnd: () => void,
): RunStream => {
  const client = useGoldenClient();
  const [steps, setSteps] = useState<readonly RunStepOut[]>([]);
  const [state, setState] = useState<string>();
  const [connection, setConnection] = useState<StreamConnection>("connecting");
  const [droppedEvents, setDroppedEvents] = useState(false);
  // The latest callback, so a new one doesn't open a new stream.
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    const unmounted = new AbortController();
    let lastSeq: number | undefined;
    let isEnded = false;
    let isRefused = false;
    let isFailing = false;
    let unauthorized = 0;
    let current: AbortController | undefined;
    const context = { caseId, runId };

    // Once only: the end stops the stream, so a replayed end does nothing.
    const end = (): void => {
      if (isEnded) return;
      isEnded = true;
      setConnection("ended");
      onEndRef.current();
      current?.abort();
    };

    // Names the event, never its contents, which hold RUMI's step data.
    const drop = (event: string): void => {
      setDroppedEvents(true);
      logger.warn("Run stream event dropped", { ...context, event });
    };

    const handle = (event: string | undefined, data: unknown): void => {
      // A comment line keeps the connection open and carries no event.
      if (event === undefined || isEnded) return;
      isFailing = false;
      unauthorized = 0;
      setConnection("live");

      if (event === "step") {
        const parsed = zStep.safeParse(data);
        if (!parsed.success) return drop(event);
        const step = parsed.data;
        lastSeq = step.seq;
        setSteps((known) =>
          known.some((s) => s.seq === step.seq) ? known : [...known, step],
        );
      } else if (event === "run") {
        const parsed = zRunEvent.safeParse(data);
        if (!parsed.success) return drop(event);
        setState(parsed.data.state);
      } else if (event === "end") {
        if (!zEndEvent.safeParse(data).success) return drop(event);
        end();
      }
    };

    const fail = (error: unknown, signal: AbortSignal): void => {
      // An abort is the page leaving or the run's end: not a failure.
      if (signal.aborted) return;
      const status = failedStatus(error);
      if (status === 401) unauthorized += 1;
      const isTerminal =
        status !== undefined &&
        (TERMINAL_STATUSES.has(status) ||
          (status === 401 && unauthorized > UNAUTHORIZED_RETRIES));
      if (isTerminal) {
        isRefused = true;
        setConnection("unavailable");
        logger.warn("Run stream refused", { ...context, status });
        current?.abort();
        return;
      }
      setConnection("reconnecting");
      // Logged once per run of failures; the error's text can name the URL.
      if (!isFailing) logger.warn("Run stream failed", { ...context, status });
      isFailing = true;
    };

    const follow = async (): Promise<void> => {
      while (!unmounted.signal.aborted && !isEnded && !isRefused) {
        const attempt = new AbortController();
        current = attempt;
        const stopAttempt = (): void => attempt.abort();
        unmounted.signal.addEventListener("abort", stopAttempt);
        try {
          const { stream } = await streamCaseRun({
            client,
            path: { case_id: caseId, run_id: runId },
            headers:
              lastSeq === undefined ? undefined : { "Last-Event-ID": lastSeq },
            signal: attempt.signal,
            // The spec types the stream as text, and the generated validator
            // would refuse each event's JSON; the events are checked in handle.
            responseValidator: undefined,
            // One attempt per connection: the helper's own retry would resume
            // from the last id it parsed, which can be an event handle dropped,
            // and reuse the token this connection started with. This loop
            // reconnects instead, after the last step it accepted, with a
            // fresh token.
            sseMaxRetryAttempts: 1,
            onSseError: (error) => fail(error, attempt.signal),
            onSseEvent: (message) => handle(message.event, message.data),
          });
          // The events arrive through onSseEvent; this only drains the stream.
          while (!(await stream.next()).done) continue;
        } catch (error: unknown) {
          fail(error, attempt.signal);
        } finally {
          unmounted.signal.removeEventListener("abort", stopAttempt);
        }
        if (isEnded || isRefused || unmounted.signal.aborted) break;
        // Closed or given up before the end: connect again after the last step.
        setConnection("reconnecting");
        await sleep(STREAM_RECONNECT_MS, unmounted.signal);
      }
    };

    void follow();
    return () => unmounted.abort();
  }, [client, caseId, runId]);

  return { steps, state, connection, droppedEvents };
};
